// File: frontend/src/types/adminMatch.types.ts
// src/types/adminMatch.types.ts

import { MatchGenderEnum } from "../models/enums/MatchGenderEnum";
import { MatchStatusEnum } from "../models/enums/MatchStatusEnum";
import { SportTypeEnum } from "../models/enums/SportTypeEnum";

/**
 * Team info within admin match response.
 * Contains both the team (sport branch) and its parent club details.
 */
export interface AdminMatchTeamInfo {
  teamId: number;
  sportType: SportTypeEnum;
  gender: MatchGenderEnum;
  // Parent club details
  clubId: number;
  clubShortName: string;
  clubLongName: string | null;
  clubAbbreviation: string | null;
  clubSlug: string | null;
  clubLogoUrl: string | null;
  clubCountry: string;
  /**
   * Historical snapshot of the team name at match creation time.
   * Use this for display — it never changes even if clubShortName is edited.
   * Falls back to clubShortName when null (old matches before the snapshot column).
   */
  snapshotName: string;
}

/**
 * User info within admin match response.
 */
export interface AdminMatchUserInfo {
  id: number;
  username: string;
}

/**
 * All possible referee role values — must match backend MatchRefereeRole enum exactly.
 * Which roles are shown in the UI depends on the match's sportType (see SPORT_REFEREE_ROLES).
 */
export type MatchRefereeRole =
  // Shared / generic
  | "MAIN"
  | "ASSISTANT_1"
  | "ASSISTANT_2"
  | "FOURTH_OFFICIAL"
  | "OTHER"
  // Football
  | "VAR"
  | "AVAR"
  // Basketball
  | "CREW_CHIEF"
  | "REFEREE"
  | "UMPIRE"
  // Volleyball
  | "FIRST_REFEREE"
  | "SECOND_REFEREE"
  | "SCORER"
  | "ASSISTANT_SCORER"
  | "LINE_JUDGE"
  // Handball
  | "REFEREE_1"
  | "REFEREE_2"
  | "TABLE_OFFICIAL"
  | "TIMEKEEPER"
  // Ice Hockey
  | "LINESMAN_1"
  | "LINESMAN_2"
  // Rugby
  | "TMO"
  // Baseball
  | "HOME_PLATE_UMPIRE"
  | "BASE_UMPIRE_1"
  | "BASE_UMPIRE_2"
  | "BASE_UMPIRE_3";

/**
 * Per-sport mapping of which referee roles are valid.
 * Used by the admin UI to filter the role dropdown based on the match's sportType.
 * The first role in each array is used as the default selection when the modal opens.
 */
export const SPORT_REFEREE_ROLES: Record<SportTypeEnum, MatchRefereeRole[]> = {
  [SportTypeEnum.FOOTBALL]: [
    "MAIN", "ASSISTANT_1", "ASSISTANT_2", "FOURTH_OFFICIAL", "VAR", "AVAR", "OTHER",
  ],
  [SportTypeEnum.BASKETBALL]: [
    "CREW_CHIEF", "REFEREE", "UMPIRE", "OTHER",
  ],
  [SportTypeEnum.VOLLEYBALL]: [
    "FIRST_REFEREE", "SECOND_REFEREE", "SCORER", "ASSISTANT_SCORER", "LINE_JUDGE", "OTHER",
  ],
  [SportTypeEnum.HANDBALL]: [
    "REFEREE_1", "REFEREE_2", "TABLE_OFFICIAL", "TIMEKEEPER", "OTHER",
  ],
  [SportTypeEnum.ICE_HOCKEY]: [
    "REFEREE_1", "REFEREE_2", "LINESMAN_1", "LINESMAN_2", "OTHER",
  ],
  [SportTypeEnum.BASEBALL]: [
    "HOME_PLATE_UMPIRE", "BASE_UMPIRE_1", "BASE_UMPIRE_2", "BASE_UMPIRE_3", "OTHER",
  ],
  [SportTypeEnum.AMERICAN_FOOTBALL]: [
    "MAIN", "ASSISTANT_1", "ASSISTANT_2", "FOURTH_OFFICIAL", "OTHER",
  ],
};

/**
 * Returns the default role to pre-select when the referee add modal opens,
 * based on the match's sport type.
 */
export const getDefaultRefereeRole = (sportType: SportTypeEnum): MatchRefereeRole => {
  return SPORT_REFEREE_ROLES[sportType]?.[0] ?? "OTHER";
};

/**
 * Sports that support extra time (ET/overtime).
 * Volleyball does not use ET periods (rally scoring decides).
 */
export const SPORTS_WITH_EXTRA_TIME = new Set<SportTypeEnum>([
  SportTypeEnum.FOOTBALL,
  SportTypeEnum.BASKETBALL,
  SportTypeEnum.ICE_HOCKEY,
  SportTypeEnum.HANDBALL,
  SportTypeEnum.AMERICAN_FOOTBALL,
  SportTypeEnum.BASEBALL,
]);

/**
 * Sports that support penalty shootouts.
 * Basketball, American football, rugby and baseball do NOT use penalty shootouts.
 */
export const SPORTS_WITH_PENALTY = new Set<SportTypeEnum>([
  SportTypeEnum.FOOTBALL,
  SportTypeEnum.ICE_HOCKEY,
  SportTypeEnum.HANDBALL,
]);

/**
 * Sports where multiple OT periods can occur (basketball, ice hockey).
 * For these we show a period counter in addition to ET scores.
 */
export const SPORTS_WITH_MULTI_OT = new Set<SportTypeEnum>([
  SportTypeEnum.BASKETBALL,
  SportTypeEnum.ICE_HOCKEY,
]);

/**
 * Referee assignment for a match — mirrors AdminMatchDto.RefereeInfo on the backend.
 */
export interface MatchRefereeInfo {
  // match_referees.id — used for DELETE
  assignmentId: number;
  personId: number;
  displayName: string;
  slug: string;
  photoUrl: string | null;
  refereeRole: MatchRefereeRole;
}

/**
 * Request body for POST /api/admin/matches/{id}/referees
 */
export interface SaveMatchRefereeRequest {
  personId: number;
  refereeRole: MatchRefereeRole;
}

/**
 * Admin match DTO matching backend AdminMatchDto response.
 */
export interface AdminMatchDto {
  id: number;
  homeTeam: AdminMatchTeamInfo;
  awayTeam: AdminMatchTeamInfo;
  homeScore: number;
  awayScore: number;
  // Extra time / overtime scores (null = not played)
  homeScoreEt: number | null;
  awayScoreEt: number | null;
  // Penalty shootout scores (null = no shootout)
  homeScorePen: number | null;
  awayScorePen: number | null;
  // Number of overtime periods played (0 = none; mainly for basketball)
  overtimePeriods: number;
  matchDate: string;
  matchTime: string;
  stadiumName: string;
  sportType: SportTypeEnum;
  matchGender: MatchGenderEnum;
  league: string | null;
  season: string | null;
  round: string | null;
  status: MatchStatusEnum;
  // Whether the match is visible on the public site (left pane, search)
  active: boolean;
  entryCount: number;
  // Referee assignments for this match — loaded from match_referees table
  referees: MatchRefereeInfo[];
  createdBy: AdminMatchUserInfo | null;
  createdAt: string;
  updatedBy: AdminMatchUserInfo | null;
  updatedAt: string | null;
  // Whether new entry submissions are currently blocked for this match page
  isLocked: boolean;
  lockReason: string | null;
  lockReasonVisible: boolean;
  lockedByUsername: string | null;
  lockedAt: string | null;
}

export interface AdminMatchPageResponse {
  content: AdminMatchDto[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

export interface AdminMatchFilters {
  status?: MatchStatusEnum | "";
  sportType?: SportTypeEnum | "";
  matchGender?: MatchGenderEnum | "";
  // Club country code ("" = all). "INTL" selects matches between clubs of
  // different countries; any other code selects matches where BOTH clubs
  // belong to that country.
  country?: string;
  // Public visibility filter: "" = all, "true" = visible, "false" = hidden
  active?: "" | "true" | "false";
  // Lock status filter: "" = all, "true" = locked, "false" = not locked
  locked?: "" | "true" | "false";
  search?: string;
  startDate?: string;
  endDate?: string;
  sortBy: string;
  sortDir: "asc" | "desc";
}

/**
 * Request to create a new match.
 * homeTeamId and awayTeamId now refer to Team IDs (sport branches), not Club IDs.
 */
export interface CreateMatchByAdminRequest {
  homeTeamId: number;
  awayTeamId: number;
  matchDate: string;
  matchTime: string;
  stadiumName?: string;
  sportType?: SportTypeEnum;
  matchGender?: MatchGenderEnum;
  league?: string;
  season?: string;
  round?: string;
  homeScore?: number;
  awayScore?: number;
  status?: MatchStatusEnum;
  // Extra time / overtime scores (null = not played)
  homeScoreEt?: number | null;
  awayScoreEt?: number | null;
  // Penalty shootout scores (null = no shootout)
  homeScorePen?: number | null;
  awayScorePen?: number | null;
  // Number of OT periods (0 = none)
  overtimePeriods?: number;
}

export interface UpdateMatchByAdminRequest {
  homeTeamId?: number;
  awayTeamId?: number;
  matchDate?: string;
  matchTime?: string;
  stadiumName?: string;
  sportType?: SportTypeEnum;
  matchGender?: MatchGenderEnum;
  league?: string;
  season?: string;
  round?: string;
  status?: MatchStatusEnum;
  // Public visibility toggle — undefined keeps the existing value
  active?: boolean;
  homeScore?: number;
  awayScore?: number;
  // Extra time / overtime scores (null = clear the column)
  homeScoreEt?: number | null;
  awayScoreEt?: number | null;
  // Penalty shootout scores (null = clear the column)
  homeScorePen?: number | null;
  awayScorePen?: number | null;
  // Number of OT periods
  overtimePeriods?: number;
}

/**
 * Request to update match score including optional ET and penalty shootout scores.
 * null for ET/penalty fields means "not played" — clears those columns in the DB.
 */
export interface UpdateMatchScoreRequest {
  homeScore: number;
  awayScore: number;
  // Extra time scores — null clears the column (ET not played)
  homeScoreEt: number | null;
  awayScoreEt: number | null;
  // Penalty shootout scores — null clears the column (no shootout)
  homeScorePen: number | null;
  awayScorePen: number | null;
  // Number of OT periods (0 = none)
  overtimePeriods: number;
}

export interface UpdateMatchStatusRequest {
  status: MatchStatusEnum;
}